import { ChangeDetectorRef, Component, HostBinding, Input, OnInit } from '@angular/core'
import { Actions, ofType } from '@ngrx/effects'
import { Store, select } from '@ngrx/store'
import { selectInvoice, UpdateInvoiceStatusComplete } from '@tokeet-frontend/invoices'
import {
  Destroyable,
  distinctObjectUntilChangedByFields,
  Rental,
  selectRentalById,
  selectSomeOnce,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import { InquiryChargeService } from '@tv3/services/inquiry-charge.service'
import { InquirySourceType } from '@tv3/store/inquiry/inquiry-source-view'
import { LoadInquiryDetails } from '@tv3/store/inquiry/inquiry.actions'
import { getBookingPaymentStatus, Inquiry, InquiryPaymentStatus } from '@tv3/store/inquiry/inquiry.model'
import { selectInquiry } from '@tv3/store/inquiry/inquiry.selectors'
import { BehaviorSubject, merge } from 'rxjs'
import { debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators'

@Component({
  selector: 'app-booking-paid-status',
  template: `
    <ng-container *ngIf="status$ | async as status">
      <span
        [matTooltip]="
          remaining && status?.remaining
            ? remainingText + ': ' + (status?.remaining | tokeetCurrency : rental?.currency)
            : (getPaymentStatusTooltip | call : inquiry : status?.status)
        "
        [matTooltipDisabled]="tooltipDisabled"
      >
        {{ getStatusText(instantly ? status?.status : paymentStatus) }}
      </span>
    </ng-container>
  `,
  styles: [
    `
      :host {
        display: inline-block;
      }
    `,
  ],
})
export class BookingPaidStatusComponent extends Destroyable implements OnInit {
  @Input() pill = true
  @Input() remaining = true
  @Input() tooltipDisabled = false
  @Input() instantly = true

  private _inquiry$ = new BehaviorSubject<Inquiry>(null)
  @Input() set inquiry(inquiry: Inquiry) {
    this._inquiry$.next(inquiry)
  }
  get inquiry() {
    return this._inquiry$.value
  }

  get paymentStatus() {
    return getBookingPaymentStatus(this.inquiry)
  }

  remainingText = $localize`Remaining`

  @HostBinding('class') get hostClass() {
    if (!this.pill) return ''
    const status = this.instantly ? this.status$.value?.status : this.paymentStatus
    return `badge badge-pill ${status === InquiryPaymentStatus.Paid ? 'badge-success' : 'badge-danger'}`
  }

  rental: Rental

  status$ = new BehaviorSubject<{ paid: boolean; remaining: number; status: InquiryPaymentStatus }>(null)

  constructor(
    private store: Store<any>,
    private actions$: Actions,
    private inquiryChargeService: InquiryChargeService
  ) {
    super()
  }

  ngOnInit(): void {
    const booking$ = this._inquiry$.pipe(filter((inquiry) => !!inquiry))
    booking$
      .pipe(
        switchMap((b) => this.store.pipe(select(selectRentalById(b.rentalId)))),
        untilDestroy(this)
      )
      .subscribe((rental) => {
        this.rental = rental
      })

    merge(
      booking$.pipe(
        distinctObjectUntilChangedByFields(['id', 'payment_status', 'paid']),
        // The first emission is just the initial render — serve it from the 30s request cache;
        // only actual payment-field changes need to bypass it
        map((inquiry, index) => ({ inquiry, refresh: index > 0 }))
      ),
      this.actions$.pipe(
        ofType(UpdateInvoiceStatusComplete),
        switchMap((d) => this.store.pipe(selectSomeOnce(selectInvoice, { id: d.update.id }))),
        filter((i) => i.inquiryId === this.inquiry?.id),
        switchMap((invoice) => this.store.pipe(selectSomeOnce(selectInquiry, { id: invoice.inquiryId }))),
        map((inquiry) => ({ inquiry, refresh: true })),
        tap((d) => this.store.dispatch(LoadInquiryDetails({ id: d.inquiry.id })))
      )
    )
      .pipe(
        debounceTime(1000),
        switchMap(({ inquiry, refresh }) =>
          this.inquiryChargeService.getBookingPaymentStatusForInquiry(inquiry, refresh)
        )
      )
      .subscribe((status) => this.status$.next(status))
  }

  getStatusText(status: InquiryPaymentStatus) {
    switch (status) {
      case InquiryPaymentStatus.Paid:
        return $localize`Paid`
      case InquiryPaymentStatus.PartialPaid:
        return $localize`Partial Paid`
      case InquiryPaymentStatus.Unpaid:
      default:
        return $localize`Unpaid`
    }
  }

  getPaymentStatusTooltip(inquiry: Inquiry, status: InquiryPaymentStatus) {
    if (inquiry?.paid && status !== InquiryPaymentStatus.Paid) return $localize`Marked as Paid`
    if (inquiry?.paid && status == InquiryPaymentStatus.Paid) return $localize`Booking is Paid`
    switch (inquiry?.sourceType) {
      case InquirySourceType.Airbnb:
      case InquirySourceType.Vrbo:
      case InquirySourceType.FlipKey:
        // tslint:disable-next-line:max-line-length
        return $localize`Payment status for this channel is taken directly from the originating channel.  When a booking is paid on the channel website, this status will update accordingly.`
      case InquirySourceType.Booking:
      case InquirySourceType.Expedia:
      case InquirySourceType.Agoda:
        // tslint:disable-next-line:max-line-length
        return $localize`The payment status reflected here does not account for any payments made outside of AdvanceCM invoicing.  Once the total of paid AdvanceCM invoices equals the booking charges shown on the booking, the status will automatically change to paid.  If you do not want to invoice through AdvanceCM, you can manually mark the booking as paid by using the Actions button on the booking details overlay.`
      default:
        // tslint:disable-next-line:max-line-length
        return $localize`The payment status reflected here does not account for any payments made on the channel listing site.  You can either create an invoice that equals the booking charges shown or manually mark the booking as paid by using the Actions button on the booking details overlay.`
    }
  }
}
